import { supabase } from "~/integrations/supabase/client";
import type {
  AccountJournal,
  JournalFilters,
  JournalFormData,
} from "~/types/contabilidad";

export const journalService = {
  async getAll(filters?: JournalFilters): Promise<AccountJournal[]> {
    let query = supabase
      .from("account_journal")
      .select("*, company:company_id(*)")
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.type) {
      query = query.eq("type", filters.type);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query.order("created_at", {
      ascending: false,
    });

    if (error) throw error;
    return data as AccountJournal[];
  },

  async getByCompany(companyId: string): Promise<AccountJournal[]> {
    const { data, error } = await supabase
      .from("account_journal")
      .select("*")
      .eq("company_id", companyId)
      .eq("is_deleted", false)
      .order("code", { ascending: true });

    if (error) throw error;
    return data as AccountJournal[];
  },

  async getById(id: string): Promise<AccountJournal> {
    const { data, error } = await supabase
      .from("account_journal")
      .select("*, company:company_id(*)")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as AccountJournal;
  },

  async create(formData: JournalFormData): Promise<AccountJournal> {
    const { data, error } = await supabase
      .from("account_journal")
      .insert(formData)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountJournal;
  },

  async update(
    id: string,
    formData: Partial<JournalFormData>
  ): Promise<AccountJournal> {
    const { data, error } = await supabase
      .from("account_journal")
      .update(formData)
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountJournal;
  },

  async delete(id: string): Promise<void> {
    // Soft delete
    const { error } = await supabase
      .from("account_journal")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) throw error;
  },
};
